import React, { memo, useCallback, useMemo, useState } from "react";
import { withNamespaces } from "react-i18next";
import { useHistory } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory, {
    PaginationProvider,
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import Spinner from "react-bootstrap/Spinner";
import {
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
} from "react-bootstrap";
import { Button, Col, Row, UncontrolledTooltip } from "reactstrap";

import TableFilter from "src/components/TableFilter";
import { EMPTY_LIST } from "src/common/constants";
import { ROUTE_PATH_ADMIN_USER_LIST } from "src/modules/data-protection/constants";

import CreateFunctionModal from "../create-function-modal";
import useOrganizationFunctions from "./hooks/useOrganizationFunctions";
import {
    EMPTY_VALUE,
    FUNCTION_STATUS,
    filterFunctions,
    formatAssignedUserRole,
    formatFunctionDate,
    formatFunctionDescription,
    getAssignedUserDisplayName,
    shouldShowAssignedRoleColumn,
    sortFunctions,
} from "./helpers";

const FunctionsTable = ({ t }) => {
    const history = useHistory();
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [search, setSearch] = useState("");
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [moreActionsDropDownOpen, setMoreActionsDropDownOpen] = useState(false);

    const toggleMoreActionsDropDown = () =>
        setMoreActionsDropDownOpen((open) => !open);

    const functionsQuery = useOrganizationFunctions(t);
    const functions = useMemo(() => functionsQuery.data || [], [functionsQuery.data]);

    const filteredFunctions = useMemo(
        () => sortFunctions(filterFunctions(functions, search), sortField, sortOrder),
        [functions, search, sortField, sortOrder]
    );

    const pagedFunctions = useMemo(() => {
        const start = (pageIndex - 1) * pageSize;

        return filteredFunctions.slice(start, start + pageSize);
    }, [filteredFunctions, pageIndex, pageSize]);

    const showAssignedRoleColumn = shouldShowAssignedRoleColumn(functions);

    const handleSearchChange = useCallback((value) => {
        setSearch(value || "");
        setPageIndex(1);
    }, []);

    const handleTableChange = useCallback((type, options) => {
        const { page, sizePerPage, sortField: nextSortField, sortOrder: nextSortOrder } = options;

        switch (type) {
            case "pagination":
                setPageIndex(page);
                setPageSize(sizePerPage);
                break;

            case "sort":
                setSortField(nextSortField);
                setSortOrder(nextSortOrder);
                break;

            default:
                return false;
        }
    }, []);

    const NoDataIndication = () =>
        functionsQuery.isFetched && !filteredFunctions.length ? (
            <div className="alert m-0" role="alert">
                <p
                    style={{
                        textAlign: "center",
                        marginBottom: 0,
                    }}
                >
                    {t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const tableColumns = useMemo(() => {
        const columns = [
            {
                dataField: "title",
                text: t("Function"),
                sort: true,
                style: {
                    width: "180px",
                },
                formatter: (_cellContent, row) => row.title || EMPTY_VALUE,
            },
            {
                dataField: "description",
                text: t("Description"),
                sort: true,
                style: {
                    width: "220px",
                },
                formatter: (_cellContent, row) => formatFunctionDescription(row.description),
            },
            {
                dataField: "assigned_users",
                text: t("Assigned User"),
                sort: false,
                style: {
                    width: "200px",
                },
                formatter: (cellContent) => {
                    const users = (Array.isArray(cellContent) ? cellContent : []).filter(
                        (user) => getAssignedUserDisplayName(user)
                    );

                    if (!users.length) {
                        return EMPTY_VALUE;
                    }

                    return (
                        <div>
                            {users.map((user) => (
                                <div key={user.id}>
                                    {getAssignedUserDisplayName(user)}
                                </div>
                            ))}
                        </div>
                    );
                },
            },
        ];

        if (showAssignedRoleColumn) {
            columns.push({
                dataField: "assigned_role",
                text: t("Assigned Role"),
                sort: false,
                style: {
                    width: "160px",
                },
                formatter: (_cellContent, row) => {
                    const roles = (row.assigned_users || [])
                        .map((user) => formatAssignedUserRole(user, t))
                        .filter((role) => role && role !== EMPTY_VALUE);

                    return roles.length ? roles.join(", ") : EMPTY_VALUE;
                },
            });
        }

        columns.push(
            {
                dataField: "created_at",
                text: t("Created On"),
                sort: true,
                style: {
                    width: "120px",
                },
                formatter: (_cellContent, row) => (
                    <div>{formatFunctionDate(row.created_at)}</div>
                ),
            },
            {
                dataField: "updated_at",
                text: t("Updated On"),
                sort: true,
                style: {
                    width: "120px",
                },
                formatter: (_cellContent, row) => (
                    <div>{formatFunctionDate(row.updated_at)}</div>
                ),
            },
            {
                dataField: "status",
                text: t("Status"),
                sort: true,
                style: {
                    width: "80px",
                },
                formatter: (cellContent) => {
                    const status = FUNCTION_STATUS[cellContent];

                    if (!status) {
                        return EMPTY_VALUE;
                    }

                    return (
                        <div>
                            <Button color={status.color} size="sm">
                                {t(status.label)}
                            </Button>
                        </div>
                    );
                },
            },
            {
                dataField: "actions",
                text: t("Actions"),
                sort: false,
                style: {
                    width: "80px",
                },
                formatter: (_cellContent, row) => {
                    const buttonId = `view-function-button-${row.id || row.key}`;

                    return (
                        <div>
                            <Button
                                color="link"
                                className="waves-effect"
                                type="button"
                                id={buttonId}
                                onClick={() =>
                                    history.push(
                                        `${ROUTE_PATH_ADMIN_USER_LIST}/functions/${row.key}`
                                    )
                                }
                            >
                                <i className="ri-eye-line"></i>
                            </Button>
                            <UncontrolledTooltip target={buttonId}>
                                {t("View")}
                            </UncontrolledTooltip>
                        </div>
                    );
                },
            }
        );

        return columns;
    }, [history, showAssignedRoleColumn, t]);

    const tableFilterTabs = useMemo(
        () => [
            {
                type: "search_textbox",
                name: "q",
                title: "Search",
                icon: <i className="ri-search-2-line" />,
                defaultValue: search,
                options: {
                    placeholder: "Search in functions",
                    typingDelay: 750,
                },
                displayIndependently: true,
                onFilterChange: handleSearchChange,
            },
        ],
        [handleSearchChange, search]
    );

    return (
        <div className="p-4" style={{ marginBottom: "300px" }}>
            <PaginationProvider
                pagination={paginationFactory({
                    custom: true,
                    page: pageIndex,
                    sizePerPage: pageSize,
                    totalSize: filteredFunctions.length,
                    withFirstAndLast: false,
                    alwaysShowAllBtns: true,
                    prePageText: (
                        <span>
                            <i className="ri-arrow-left-s-line"></i> {t("Back")}
                        </span>
                    ),
                    nextPageText: (
                        <span>
                            {t("Next")} <i className="ri-arrow-right-s-line"></i>
                        </span>
                    ),
                    prePageTitle: t("Pre page"),
                    firstPageTitle: t("Next page"),
                    showTotal: true,
                    hideSizePerPage: false,
                    sizePerPageList: [
                        {
                            text: "25",
                            value: 25,
                        },
                        {
                            text: "50",
                            value: 50,
                        },
                    ],
                })}
            >
                {({ paginationProps, paginationTableProps }) => {
                    return (
                        <React.Fragment>
                            <Row className="mb-3 mt-3">
                                <Col sm="12" md="6" />
                                <Col
                                    sm="12"
                                    md="6"
                                    className="d-flex align-items-center justify-content-end"
                                >
                                    <TableFilter
                                        tabs={tableFilterTabs}
                                        onOkButtonClicked={() => {}}
                                    />
                                    <Dropdown
                                        isOpen={moreActionsDropDownOpen}
                                        toggle={toggleMoreActionsDropDown}
                                    >
                                        <DropdownToggle
                                            caret
                                            variant="outline-secondary"
                                            outline
                                        >
                                            {t("Actions")}
                                        </DropdownToggle>
                                        <DropdownMenu className="dropdown-menu-end">
                                            <DropdownItem
                                                onClick={() => setIsCreateOpen(true)}
                                            >
                                                <i className="ri-add-line align-middle me-2" />
                                                {t("Create Function")}
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12">
                                    <BootstrapTable
                                        remote={{
                                            pagination: true,
                                            filter: false,
                                            sort: true,
                                            cellEdit: false,
                                            search: false,
                                        }}
                                        loading={
                                            functionsQuery.isFetching ||
                                            functionsQuery.isLoading
                                        }
                                        overlay={overlayFactory({
                                            spinner: (
                                                <Spinner
                                                    animation="border"
                                                    variant="primary"
                                                    size="md"
                                                />
                                            ),
                                            text: "Loading...",
                                        })}
                                        onTableChange={handleTableChange}
                                        defaultSorted={[]}
                                        keyField={"key"}
                                        responsive
                                        bordered={false}
                                        data={pagedFunctions}
                                        striped={true}
                                        columns={tableColumns}
                                        wrapperClasses="table-responsive"
                                        classes={"table tpdd-table"}
                                        headerWrapperClasses={"thead-light"}
                                        style={{
                                            overflowX: "auto",
                                        }}
                                        noDataIndication={() => <NoDataIndication />}
                                        {...paginationTableProps}
                                    />
                                </Col>
                            </Row>

                            <Row>
                                <Col sm="12" md="6">
                                    <div className="tpdd-pagination-style-1">
                                        <PaginationListStandalone {...paginationProps} />
                                        <SizePerPageDropdownStandalone {...paginationProps} />
                                    </div>
                                </Col>
                            </Row>
                        </React.Fragment>
                    );
                }}
            </PaginationProvider>
            <CreateFunctionModal
                isOpen={isCreateOpen}
                toggle={() => setIsCreateOpen(false)}
            />
        </div>
    );
};

export default withNamespaces()(memo(FunctionsTable));
